import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { unregisterServiceWorker } from './registerServiceWorker';
import configureStore from './store/configureStore';

// Load Google Fonts dynamically from env variable
const fontFamily = import.meta.env.VITE_FontFamilyDownload || 'Poppins';
if (fontFamily && fontFamily !== '__FONTFAMILY__') {
    const fontUrl = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap`;

    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.href = fontUrl;
    preloadLink.as = 'style';
    preloadLink.crossOrigin = 'anonymous';
    document.head.appendChild(preloadLink);

    const styleLink = document.createElement('link');
    styleLink.rel = 'stylesheet';
    styleLink.href = fontUrl;
    document.head.appendChild(styleLink);
}

import { ContextProvider, contextSaga, ReferenceProvider, referenceSaga, runCoreSaga } from 'metrix-common3';
import { HelmetProvider } from 'react-helmet-async';

import App from './containers/App';
import { enMessages, frMessages } from './messages';
import AppWithConstants from './provider/constants';

function getCookie(name) {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : null;
}

// Get language preference with a fallback chain
const preferredCulture = getCookie('preferredCulture') || localStorage.getItem('preferredCulture') || 'en-CA';

// Override fetch to use the preferred culture
const originalFetch = window.fetch;
window.fetch = (url, options = {}) => {
    if (!options.headers) {
        options.headers = {};
    }

    options.headers = {
        'Accept-Language': preferredCulture,
        ...options.headers,
    };

    return originalFetch(url, options);
};

const initialState = window.initialReduxState;

const store = configureStore(initialState);

// Run sagas. Through the injector rather than store.runSaga: the providers inject these same
// sagas on mount, and a bare runSaga leaves no key behind for that injection to find, so it
// started a second watcher and every context action was handled twice.
runCoreSaga(store, 'context', contextSaga);
runCoreSaga(store, 'reference', referenceSaga);

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <h2>Something went wrong.</h2>
                    <p>{this.state.error?.message}</p>
                    <button type='button' onClick={() => window.location.reload()}>
                        Refresh
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

const rootElement = document.getElementById('app');
const root = createRoot(rootElement);

root.render(
    <ErrorBoundary>
        <HelmetProvider>
            <Provider store={store}>
                <AppWithConstants>
                    <ContextProvider messages={{ en: enMessages, fr: frMessages }}>
                        <ReferenceProvider>
                            <App />
                        </ReferenceProvider>
                    </ContextProvider>
                </AppWithConstants>
            </Provider>
        </HelmetProvider>
    </ErrorBoundary>,
);

unregisterServiceWorker();
